import { z } from 'zod';
import { NoteVisibilitySchema } from './note';

// =====================
// Note Templates
// =====================
// Org-scoped templates instantiated into notes by copying Yjs state.
// System starter templates are code-defined (SYSTEM_NOTE_TEMPLATES below),
// not database rows; their ids carry the `system:` prefix.

export const SYSTEM_NOTE_TEMPLATE_PREFIX = 'system:';

export const isSystemNoteTemplateId = (id: string): boolean =>
  id.startsWith(SYSTEM_NOTE_TEMPLATE_PREFIX);

export const NoteTemplateSchema = z.object({
  id: z.string(),
  organizationId: z.string().uuid().nullable(), // null for system templates
  name: z.string(),
  description: z.string().optional().nullable(),
  // Backing note holding the template content; edited with the full note
  // editor. Null for system templates and legacy rows without one.
  noteId: z.string().uuid().optional().nullable(),
  contentHtml: z.string().optional().nullable(),
  isSystem: z.boolean(),
  createdBy: z.string().uuid().optional().nullable(),
  creatorName: z.string().optional().nullable(),
  usageCount: z.number().default(0),
  lastUsedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
});

// Also used for creating a blank template (POST /note-templates): the content
// is authored afterwards in the template content editor.
export const CreateNoteTemplateFromNoteSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
});

export const UpdateNoteTemplateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(2000).nullable().optional(),
});

export const InstantiateNoteTemplateSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  visibility: NoteVisibilitySchema.optional(),
  moduleType: z.string().optional(),
  moduleId: z.string().uuid().optional(),
  categoryId: z.string().uuid().optional(),
});

export type NoteTemplate = z.infer<typeof NoteTemplateSchema>;
export type CreateNoteTemplateFromNote = z.infer<typeof CreateNoteTemplateFromNoteSchema>;
export type UpdateNoteTemplate = z.infer<typeof UpdateNoteTemplateSchema>;
export type InstantiateNoteTemplate = z.infer<typeof InstantiateNoteTemplateSchema>;

// =====================
// System starter templates
// =====================
// Authored as HTML because the server-side converter's TipTap schema is
// StarterKit + TaskList (see apps/api yjs-content.service.ts) — task-list
// checkboxes need the data-type markup, which markdown cannot express.

export interface SystemNoteTemplate {
  id: string;
  name: string;
  description: string;
  html: string;
}

export const SYSTEM_NOTE_TEMPLATES: SystemNoteTemplate[] = [
  {
    id: 'system:sop',
    name: 'Standard Operating Procedure',
    description: 'Step-by-step procedure with purpose, scope, and revision history.',
    html: [
      '<h1>Standard Operating Procedure</h1>',
      '<h2>Purpose</h2>',
      '<p>Describe what this procedure accomplishes and why it exists.</p>',
      '<h2>Scope</h2>',
      '<p>Who this applies to, and when it should be followed.</p>',
      '<h2>Prerequisites</h2>',
      '<ul><li>Access or permissions required</li><li>Tools or materials needed</li></ul>',
      '<h2>Procedure</h2>',
      '<ol>',
      '<li><p><strong>Step name.</strong> Describe the action to take. Add a screenshot below the step if it helps.</p></li>',
      '<li><p><strong>Step name.</strong> Keep each step to a single action where possible.</p></li>',
      '<li><p><strong>Step name.</strong> Note the expected result so the reader can confirm they are on track.</p></li>',
      '</ol>',
      '<h2>Troubleshooting</h2>',
      '<p>Common problems and how to resolve them.</p>',
      '<h2>Revision history</h2>',
      '<ul><li>Date, what changed, and who changed it</li></ul>',
    ].join(''),
  },
  {
    id: 'system:runbook',
    name: 'Runbook',
    description: 'Operational runbook for responding to a recurring situation or incident.',
    html: [
      '<h1>Runbook</h1>',
      '<h2>Overview</h2>',
      '<p>What situation this runbook covers and the expected outcome.</p>',
      '<h2>When to use</h2>',
      '<p>Triggers or symptoms that mean this runbook applies.</p>',
      '<h2>Contacts</h2>',
      '<ul><li>Owner:</li><li>Escalation:</li></ul>',
      '<h2>Steps</h2>',
      '<ol>',
      '<li><p>Verify the situation. List the checks that confirm this runbook applies.</p></li>',
      '<li><p>Take the corrective action.</p></li>',
      '<li><p>Confirm resolution and record what happened.</p></li>',
      '</ol>',
      '<h2>Rollback</h2>',
      '<p>How to undo the actions above if they make things worse.</p>',
    ].join(''),
  },
  {
    id: 'system:checklist',
    name: 'Checklist',
    description: 'Simple check-off list for repeatable multi-step work.',
    html: [
      '<h1>Checklist</h1>',
      '<p>Purpose of this checklist and when to run it.</p>',
      '<h2>Before you start</h2>',
      '<ul data-type="taskList">',
      '<li data-type="taskItem" data-checked="false">Prerequisite one</li>',
      '<li data-type="taskItem" data-checked="false">Prerequisite two</li>',
      '</ul>',
      '<h2>Steps</h2>',
      '<ul data-type="taskList">',
      '<li data-type="taskItem" data-checked="false">First step</li>',
      '<li data-type="taskItem" data-checked="false">Second step</li>',
      '<li data-type="taskItem" data-checked="false">Third step</li>',
      '</ul>',
      '<h2>Wrap up</h2>',
      '<ul data-type="taskList">',
      '<li data-type="taskItem" data-checked="false">Confirm everything is done</li>',
      '<li data-type="taskItem" data-checked="false">Notify whoever needs to know</li>',
      '</ul>',
    ].join(''),
  },
];
